import { useRef, useState, useCallback, useMemo, useEffect, useLayoutEffect } from 'react';

import isEmpty from 'lodash/isEmpty';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import sumBy from 'lodash/sumBy';

import { useStorage } from './useStorage';

type ColumnType = { [key: string]: { width: number; minWidth: number } };

// Get the total width of dynamic columns
const getTotalWidth = (columns: ColumnType) => sumBy(Object.values(columns), 'width');

export const useDynamicColumns = ({
  cacheKey,
  storageInstance,
  columns,
  selectable,
  draggable
}: {
  cacheKey: string;
  storageInstance: any;
  columns: any[];
  selectable?: boolean;
  draggable?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoGrow, setAutoGrow] = useState(false);
  const [columnWidths, setColumnWidths] = useState<ColumnType>({});
  const [containerWidth, setContainerWidth] = useState(0);
  const [resizableColumns, nonResizableColumns] = useMemo(() => partition(columns, 'resizable'), [columns]);

  const defaultColumnWidths = useMemo(
    () =>
      resizableColumns.reduce((result, { key, width, minWidth }) => {
        return { ...result, [key]: { width, minWidth } };
      }, {}),
    [resizableColumns]
  );
  const defaultStorageValue = useMemo(
    () => ({
      autoGrow: false,
      columns: defaultColumnWidths
    }),
    [defaultColumnWidths]
  );

  const {
    value: storageValue,
    loaded: storageLoaded,
    setValue: setStorageValue
  } = useStorage(storageInstance, cacheKey, defaultStorageValue);

  // Calculate total width of non resizable columns
  const fixedWidth = useMemo(
    () => 20 * Number(!!draggable) + 40 * Number(!!selectable) + sumBy(nonResizableColumns, 'width'),
    [draggable, selectable, nonResizableColumns]
  );
  // Calculate total width based on the column widths setting
  const totalWidth = useMemo(() => getTotalWidth(columnWidths), [columnWidths]);
  // Get the effective width to display dynamic columns
  const effectiveWidth = containerWidth - fixedWidth;
  // Calculate visual column widths based on the column widths setting
  const visualColumnWidths = useMemo(() => {
    if (isEmpty(columnWidths)) return {};
    if (!containerWidth || autoGrow) return columnWidths;
    const ratio = effectiveWidth / totalWidth;
    return Object.entries(columnWidths).reduce((result, [key, { width, minWidth }]) => {
      return {
        ...result,
        [key]: { width: Math.max(Number(width) * ratio, Number(minWidth)), minWidth }
      };
    }, {});
  }, [effectiveWidth, autoGrow, containerWidth, totalWidth, columnWidths]);
  // Recalculate total width based on visual column widths
  const visualTotalWidth = useMemo(() => getTotalWidth(visualColumnWidths), [visualColumnWidths]);

  const handleWindowResize = useCallback(() => {
    if (!containerRef.current) return;
    const newContainerWidth = containerRef.current.clientWidth;
    const newEffectiveWidth = newContainerWidth - fixedWidth;
    if (
      (newEffectiveWidth > visualTotalWidth && effectiveWidth < visualTotalWidth) ||
      (newEffectiveWidth < visualTotalWidth && effectiveWidth > visualTotalWidth)
    ) {
      setAutoGrow(false);
    }
    setContainerWidth(newContainerWidth);
  }, [effectiveWidth, visualTotalWidth, fixedWidth]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [handleWindowResize]);

  const getCachedColumnWidths = useCallback(() => {
    const columns = pick({ ...defaultColumnWidths, ...storageValue?.columns }, Object.keys(defaultColumnWidths));

    setAutoGrow(!!storageValue?.autoGrow);
    setColumnWidths(columns);
  }, [defaultColumnWidths, storageValue?.columns, storageValue?.autoGrow, setAutoGrow, setColumnWidths]);

  useEffect(() => {
    if (!containerRef.current) return;
    setContainerWidth(containerRef.current.clientWidth);
  }, []);

  useLayoutEffect(() => {
    storageLoaded && getCachedColumnWidths();
  }, [getCachedColumnWidths, storageLoaded]);

  const visualColumns = useMemo(() => {
    return columns.map((column) => ({ ...column, ...visualColumnWidths[column.key] }));
  }, [columns, visualColumnWidths]);

  const stringifiedVisualColumnWidths = useMemo(() => JSON.stringify(visualColumnWidths), [visualColumnWidths]);

  const onColumnResize = useCallback(
    ({ key, width, minWidth }: { key: string; width: number; minWidth: number }) => {
      const widths = { ...visualColumnWidths, [key]: { width, minWidth } };
      setAutoGrow(true);
      setColumnWidths(widths);
      setStorageValue({ autoGrow: true, columns: widths });
    },
    [stringifiedVisualColumnWidths, setAutoGrow, setColumnWidths, setStorageValue]
  );

  return {
    containerRef,
    visualColumns,
    onColumnResize
  };
};
